import { useState } from 'react';
import type { Folder } from '../types';
import { formatBytes } from '../utils';
import { Icon } from './Icon';
import { ActionMenu } from './ActionMenu';

interface FolderTreeProps {
  folders: Folder[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onCreate: (parentId: string | null) => void;
  onRename: (folder: Folder) => void;
  onMove: (folder: Folder) => void;
  onDelete: (folder: Folder) => void;
  canEdit: boolean;
  imageHosting?: boolean;
  memberLibraries?: Array<{ id: string; displayName: string; email: string }>;
  selectedMemberId?: string | null;
  onMemberSelect?: (id: string) => void;
}

function TreeNode({ folder, depth, selectedId, onSelect, onCreate, onRename, onMove, onDelete, canEdit }: Omit<FolderTreeProps, 'folders'> & { folder: Folder; depth: number }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = folder.children.length > 0;
  return (
    <li>
      <div className={`tree-row ${selectedId === folder.id ? 'is-selected' : ''}`} style={{ '--tree-depth': depth } as React.CSSProperties}>
        <button type="button" className="tree-chevron" onClick={() => setExpanded((value) => !value)} aria-label={`${expanded ? 'Collapse' : 'Expand'} ${folder.name}`} disabled={!hasChildren}>
          {hasChildren && <Icon name="chevron" size={15} className={expanded ? 'is-expanded' : ''} />}
        </button>
        <button type="button" className="tree-label" onClick={() => onSelect(folder.id)} title={folder.name}>
          <Icon name="folder" size={18} /><span>{folder.name}</span>
          {(folder.mediaCount ?? folder.videoCount ?? 0) > 0 && <small>{folder.mediaCount ?? folder.videoCount}</small>}
          {folder.trackedSubtreeBytes && folder.trackedSubtreeBytes > 0 && (
            <small className="tree-row__tracked" title="Tracked source/imported/MP4 bytes (excludes HLS renditions and thumbnails)">{formatBytes(folder.trackedSubtreeBytes)} tracked</small>
          )}
        </button>
        {canEdit && <ActionMenu label={`Actions for ${folder.name}`} triggerClassName="tree-menu" trigger={<Icon name="dots" size={17} />}>
          {close => <>
            <button type="button" onClick={() => { close(); onCreate(folder.id); }}><Icon name="plus" size={16} />New subfolder</button>
            <button type="button" onClick={() => { close(); onRename(folder); }}><Icon name="edit" size={16} />Rename</button>
            <button type="button" onClick={() => { close(); onMove(folder); }}><Icon name="move" size={16} />Move</button>
            <button type="button" className="danger" onClick={() => { close(); onDelete(folder); }}><Icon name="trash" size={16} />Delete</button>
          </>}
        </ActionMenu>}
      </div>
      {hasChildren && expanded && <ul>{folder.children.map((child) => <TreeNode key={child.id} folder={child} depth={depth + 1} selectedId={selectedId} onSelect={onSelect} onCreate={onCreate} onRename={onRename} onMove={onMove} onDelete={onDelete} canEdit={canEdit} />)}</ul>}
    </li>
  );
}

function MemberLibraryNode({
  member,
  folders,
  selectedId,
  selectedMemberId,
  onMemberSelect,
  onSelect,
  onCreate,
  onRename,
  onMove,
  onDelete,
}: {
  member: { id: string; displayName: string; email: string };
  folders: Folder[];
  selectedId: string | null;
  selectedMemberId: string | null;
  onMemberSelect: (id: string) => void;
} & Pick<FolderTreeProps, 'onSelect' | 'onCreate' | 'onRename' | 'onMove' | 'onDelete'>) {
  const [expanded, setExpanded] = useState(true);
  const memberFolders = folders.filter((folder) => folder.ownerUserId === member.id);
  return <li>
    <div className={`tree-row tree-row--member ${selectedMemberId === member.id && selectedId === null ? 'is-selected' : ''}`} style={{ '--tree-depth': 0 } as React.CSSProperties}>
      <button type="button" className="tree-chevron" onClick={() => setExpanded((value) => !value)} aria-label={`${expanded ? 'Collapse' : 'Expand'} ${member.displayName}`} disabled={!memberFolders.length}>
        {memberFolders.length > 0 && <Icon name="chevron" size={15} className={expanded ? 'is-expanded' : ''} />}
      </button>
      <button type="button" className="tree-label" onClick={() => onMemberSelect(member.id)} title={`${member.displayName} — ${member.email}`}>
        <Icon name="user" size={18} /><span>{member.displayName}</span>
      </button>
    </div>
    {expanded && memberFolders.length > 0 && <ul>{memberFolders.map((folder) => <TreeNode key={folder.id} folder={folder} depth={1} selectedId={selectedId} onSelect={onSelect} onCreate={onCreate} onRename={onRename} onMove={onMove} onDelete={onDelete} canEdit={false} />)}</ul>}
  </li>;
}

export function FolderTree(props: FolderTreeProps) {
  const groupedByMember = Boolean(props.memberLibraries?.length && props.onMemberSelect);
  return <nav className="folder-nav" aria-label={props.imageHosting ? 'Media folders' : 'Video folders'}>
    <div className={`tree-row tree-row--root ${props.selectedId === null && (!groupedByMember || !props.selectedMemberId) ? 'is-selected' : ''}`}>
      <span className="tree-chevron" />
      <button type="button" className="tree-label" onClick={() => props.onSelect(null)}><Icon name="home" size={18} /><span>{props.imageHosting ? 'All media' : 'All videos'}</span></button>
    </div>
    <ul className="folder-tree">{groupedByMember ? props.memberLibraries!.map((member) => <MemberLibraryNode key={member.id} member={member} folders={props.folders} selectedId={props.selectedId} selectedMemberId={props.selectedMemberId ?? null} onMemberSelect={props.onMemberSelect!} onSelect={props.onSelect} onCreate={props.onCreate} onRename={props.onRename} onMove={props.onMove} onDelete={props.onDelete} />) : props.folders.map((folder) => <TreeNode key={folder.id} folder={folder} depth={0} selectedId={props.selectedId} onSelect={props.onSelect} onCreate={props.onCreate} onRename={props.onRename} onMove={props.onMove} onDelete={props.onDelete} canEdit={props.canEdit} />)}</ul>
  </nav>;
}
